/**
 * Routes the existing recommendation endpoints between the two mock stores.
 * The seeded agent's fixture recommendations live in the protection store and
 * everything else in the estate store; a review that selects both is split,
 * answered by each store, and merged - so the existing review and apply panels
 * work on any mix without knowing two stores exist.
 */
import { agentEstateStore } from "@/api/mock-data/agent-estate/store";
import type {
  ApplyPoliciesRequest,
  ApplyPoliciesResult,
  PolicyApplicationPreview,
  PolicyReview,
  PolicyReviewQuery,
} from "@/api/temp/AgentEstateService";
import { RECOMMENDATIONS } from "./seed";
import { agentProtectionStore } from "./store";

const SEEDED_IDS = new Set(RECOMMENDATIONS.map(item => item.recommendation.id));

const split = (policyIds: string[]) => ({
  seeded: policyIds.filter(id => SEEDED_IDS.has(id)),
  rest: policyIds.filter(id => !SEEDED_IDS.has(id)),
});

/** Estate preview id → the protection preview made alongside it. */
const pairedPreviews = new Map<string, string>();

export const getPolicyReview = (params: PolicyReviewQuery): PolicyReview => {
  const { seeded, rest } = split(params.policyIds);
  if (!seeded.length) return agentEstateStore.getPolicyReview(params);
  const fixture = agentProtectionStore.getPolicyReview({
    ...params,
    policyIds: seeded,
  });
  if (!rest.length && fixture) return fixture;
  const estate = agentEstateStore.getPolicyReview({ ...params, policyIds: rest });
  if (!fixture) return estate;
  const [fixtureAgent] = fixture.agents;
  return {
    ...estate,
    policies: [...fixture.policies, ...estate.policies],
    targets: [...fixture.targets, ...estate.targets],
    agents: estate.agents.map(agent =>
      agent.id === fixtureAgent?.id
        ? {
            ...agent,
            policyIds: [...fixtureAgent.policyIds, ...agent.policyIds],
            pendingPolicyIds: [
              ...(fixtureAgent.pendingPolicyIds ?? []),
              ...(agent.pendingPolicyIds ?? []),
            ],
          }
        : agent,
    ),
  };
};

export const previewPolicies = (
  params: ApplyPoliciesRequest,
): PolicyApplicationPreview => {
  const { seeded, rest } = split(params.policyIds);
  if (!seeded.length) return agentEstateStore.previewPolicies(params);
  const fixture = agentProtectionStore.previewPolicies({
    ...params,
    policyIds: seeded,
  });
  if (!rest.length && fixture) return fixture;
  const estate = agentEstateStore.previewPolicies({ ...params, policyIds: rest });
  if (!fixture) return estate;
  pairedPreviews.set(estate.id, fixture.id);
  return {
    ...estate,
    affectedAgentCount: Math.max(
      estate.affectedAgentCount,
      fixture.affectedAgentCount,
    ),
    policies: [...fixture.policies, ...estate.policies],
  };
};

export const applyPolicies = (
  params: ApplyPoliciesRequest,
): ApplyPoliciesResult | undefined => {
  const { seeded, rest } = split(params.policyIds);
  if (!seeded.length) return agentEstateStore.applyPolicies(params);
  if (!rest.length) return agentProtectionStore.applyPolicies(params);
  const fixtureParams = {
    ...params,
    policyIds: seeded,
    previewId: params.previewId && pairedPreviews.get(params.previewId),
  };
  // Everything the fixture half can refuse - including a pairing lost to a
  // reload - is checked before the estate half commits, never after.
  const fixtureHandles = agentProtectionStore.checkApply(fixtureParams);
  const estate = agentEstateStore.applyPolicies({ ...params, policyIds: rest });
  const fixture = fixtureHandles
    ? agentProtectionStore.applyPolicies(fixtureParams)
    : undefined;
  if (!estate || !fixture) return estate ?? fixture;
  if (params.previewId) pairedPreviews.delete(params.previewId);
  return { ...estate, results: [...fixture.results, ...estate.results] };
};
